import { lazy, Suspense, useEffect, useState } from 'react';
import type { FC } from 'react';
import { Avatar, Badge, Button, Col, Divider, Row, Typography } from 'antd';
import { TbArrowUpRight, TbBolt, TbPlugConnected } from 'react-icons/tb';

import { getPublicRuntimeConfig } from '@/config/runtime';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useBillingSnapshot } from '@/hooks/useBillingSnapshot';
import { FlexContentRegion, FullHeightFlexColumn, LearnMoreFooter } from '@/shared';
import VirtualizedList from '@/shared/components/lists/VirtualizedList';
import PlanTag from '@/shared/components/tags/PlanTag';
import { hasGatedFeatureAccess } from '@/shared/entitlements';
import { useUserStore } from '@/stores';
import { imgLogoStyling } from '@/theme/tokens';
import { ellipsisStyling } from '@/utilities/styles/ellipsisStyling';
import { corporateLogo } from '@/utilities/third-party/corporateLogo';

import Metadata from './meta';

const ZapierIntegrationModal = lazy(() => import('./ZapierIntegrationModal'));

const ZAPIER_SCRIPT_ID = 'zapier-elements-script';
const ZAPIER_STYLESHEET_ID = 'zapier-elements-stylesheet';
const ZAPIER_SCRIPT_SRC = 'https://cdn.zapier.com/packages/partner-sdk/v0/zapier-elements/zapier-elements.esm.js';
const ZAPIER_STYLESHEET_HREF = 'https://cdn.zapier.com/packages/partner-sdk/v0/zapier-elements/zapier-elements.css';

interface WorkflowIntegration {
    category: string;
    description: string;
    icon?: string;
    key: string;
    title: string;
    website: string;
}

const WORKFLOW_INTEGRATIONS: WorkflowIntegration[] = [
    {
        category: 'Automation',
        description:
            'Zapier is a workflow automation software that connects over 7,000 app integrations, enabling users to automate tasks and focus on important work without coding.',
        key: 'zapier',
        title: 'Zapier',
        website: 'https://zapier.com',
    },
];

const loadZapierEmbed = () => {
    if (!document.querySelector(`#${ZAPIER_STYLESHEET_ID}`)) {
        const link = document.createElement('link');
        link.id = ZAPIER_STYLESHEET_ID;
        link.rel = 'stylesheet';
        link.href = ZAPIER_STYLESHEET_HREF;
        document.head.appendChild(link);
    }

    const existingScript = document.querySelector<HTMLScriptElement>(`#${ZAPIER_SCRIPT_ID}`);

    if (existingScript) {
        return existingScript.dataset.loaded === 'true'
            ? Promise.resolve()
            : new Promise<void>((resolve, reject) => {
                  existingScript.addEventListener('load', () => resolve(), { once: true });
                  existingScript.addEventListener('error', () => reject(new Error('Failed to load Zapier embed.')), {
                      once: true,
                  });
              });
    }

    return new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');
        script.id = ZAPIER_SCRIPT_ID;
        script.type = 'module';
        script.src = ZAPIER_SCRIPT_SRC;
        script.onload = () => {
            script.dataset.loaded = 'true';
            resolve();
        };
        script.onerror = () => reject(new Error('Failed to load Zapier embed.'));
        document.head.appendChild(script);
    });
};

const Workflows: FC = (): JSX.Element => {
    const [zapierEmbedReady, setZapierEmbedReady] = useState(false);

    useEffect(() => {
        let mounted = true;

        loadZapierEmbed()
            .then(() => {
                if (mounted) {
                    setZapierEmbedReady(true);
                }
            })
            .catch(() => {
                if (mounted) {
                    setZapierEmbedReady(false);
                }
            });

        return () => {
            mounted = false;
        };
    }, []);

    const { snapshot: billingSnapshot } = useBillingSnapshot();
    const canUseZapier = hasGatedFeatureAccess(billingSnapshot, 'zapier');
    const user = useUserStore((state) => state.selectedUser);
    const zapierEmbedClientId = getPublicRuntimeConfig().zapierEmbedClientId;

    const [open, setOpen] = useState<boolean>(false);

    const { token, themeName } = useAntDesignTheme();

    return (
        <FullHeightFlexColumn>
            <Metadata />
            <FlexContentRegion overflowX='hidden' overflowY='auto'>
                <div style={{ paddingBottom: '1.5rem' }}>
                    <Typography.Title level={1}>Workflows</Typography.Title>
                    <Typography.Paragraph style={{ color: token.colorTextTertiary }}>
                        Automate your workflows in Countercyclical via Zapier.
                    </Typography.Paragraph>
                    <Divider />
                    <Row>
                        <Col flex='auto'>
                            <Typography.Title level={5}>
                                Generate Zapier API Key
                                <PlanTag plan='premium' style={{ marginLeft: '0.5rem', verticalAlign: '0.1rem' }} />
                            </Typography.Title>
                            <Typography.Paragraph>
                                <span style={{ color: token.colorTextTertiary }}>
                                    Learn more about integrating with your favorite tools
                                </span>{' '}
                                <a
                                    href='https://docs.countercyclical.io/guides/integrations'
                                    target='_blank'
                                    rel='noopener noreferrer'
                                >
                                    in our docs
                                    <TbArrowUpRight style={{ verticalAlign: '-0.15rem', marginLeft: '0.125rem' }} />
                                </a>
                            </Typography.Paragraph>
                        </Col>
                    </Row>
                    {/* {checked && ( */}
                    <>
                        <VirtualizedList
                            dataSource={WORKFLOW_INTEGRATIONS}
                            renderItem={(item) => {
                                return (
                                    <VirtualizedList.Item key={item.title}>
                                        <VirtualizedList.Item.Meta
                                            avatar={
                                                <Badge
                                                    offset={[0, 43]}
                                                    count={
                                                        <Avatar
                                                            size={'small'}
                                                            shape='square'
                                                            style={{
                                                                marginBottom: '1.5rem',
                                                                border: `0.125rem solid ${token.colorBgContainer}`,
                                                                backgroundColor: token.green1,
                                                            }}
                                                            icon={
                                                                <TbBolt
                                                                    style={{
                                                                        color: token.green5,
                                                                        verticalAlign: 0,
                                                                    }}
                                                                />
                                                            }
                                                        />
                                                    }
                                                >
                                                    <Avatar
                                                        size={'large'}
                                                        shape='square'
                                                        style={{
                                                            marginTop: '0.25rem',
                                                            borderRadius: token.borderRadius,
                                                            border: `1px solid ${token.colorBorder}`,
                                                        }}
                                                        src={item?.website && corporateLogo(item?.website)}
                                                        icon={
                                                            item?.icon && (
                                                                <img
                                                                    alt='Google Sheets'
                                                                    src={item?.icon}
                                                                    style={{
                                                                        ...imgLogoStyling,
                                                                        transform: 'scale(0.75)',
                                                                    }}
                                                                />
                                                            )
                                                        }
                                                    />
                                                </Badge>
                                            }
                                            title={<span style={{ fontWeight: 500 }}>{item.title}</span>}
                                            description={
                                                <span
                                                    style={{
                                                        color: token.colorTextSecondary,
                                                        ...ellipsisStyling,
                                                    }}
                                                >
                                                    {item.category}
                                                </span>
                                            }
                                        />
                                        <Button
                                            onClick={() => {
                                                setOpen(true);
                                            }}
                                            disabled={!canUseZapier}
                                        >
                                            <TbPlugConnected size='1rem' style={{ verticalAlign: '-0.2rem' }} />
                                            Connect
                                        </Button>
                                    </VirtualizedList.Item>
                                );
                            }}
                        />
                    </>
                    {/* )} */}
                    <Suspense fallback={null}>
                        <ZapierIntegrationModal open={open} setOpen={setOpen} />
                    </Suspense>
                    {/* <Divider style={{ margin: 0, marginTop: '0.25rem' }}>
                        <TbSlash size="1rem" style={{ verticalAlign: '-0.2rem', color: token.colorTextQuaternary }} />
                    </Divider>
                    <Row>
                        <Col span={18}>
                            <Typography.Title level={5} style={{ marginTop: '1rem', marginBottom: '1rem' }}>
                                All Integrations
                                <small>
                                    <SmallTooltip title="All third party trademarks (including logos and icons) are the property of their respective owners, which do not sponsor, authorize, or endorse this website.">
                                        <TbInfoCircle
                                            style={{ verticalAlign: '-0.075rem', marginLeft: '0.5rem', color: token.colorTextTertiary }}
                                        />
                                    </SmallTooltip>
                                </small>
                            </Typography.Title>
                        </Col>
                        <Col span={6} style={{ textAlign: 'right' }}>
                            <Button
                                type="text"
                                icon={
                                    <RiFilter3Line size="1rem" style={{ color: token.colorTextTertiary, verticalAlign: '-0.15rem' }} />
                                }
                                style={{ verticalAlign: '-1rem' }}
                            />
                        </Col>
                    </Row>
                    <Row style={{ marginBottom: '1.5rem' }}>
                        <Input
                            prefix={<TbSearch size="1rem" style={{ color: token.colorTextQuaternary, marginRight: '0.375rem' }} />}
                            placeholder="Search Integrations..."
                            allowClear={{
                                clearIcon: (
                                    <TbCircleXFilled
                                        size="1rem"
                                        style={{ verticalAlign: '-0.25rem', color: token.colorTextTertiary }}
                                    />
                                ),
                            }}
                        />
                    </Row>
                    <div style={{ maxHeight: '25rem', minHeight: '18rem', overflow: 'scroll' }}>
                        <List
                            grid={{ gutter: 0, column: 1 }}
                            dataSource={data}
                            renderItem={(item: any) => <WorkspaceIntegrationListItem key={item?.key} item={item} />}
                        />
                    </div> */}
                    <Divider />
                    <Typography.Title level={5}>
                        Automate your Workflows
                        <PlanTag plan='premium' style={{ marginLeft: '0.5rem', verticalAlign: '0.1rem' }} />
                    </Typography.Title>
                    <Typography.Paragraph>
                        <span style={{ color: token.colorTextTertiary }}>
                            Leverage pre-built workflows & custom integrations with your favorite apps.
                        </span>
                    </Typography.Paragraph>
                    <div
                        style={{
                            minHeight: '35rem',
                            height: 'auto',
                            border: `1px solid ${token.colorBorder}`,
                            borderRadius: token.borderRadius,
                            padding: '1rem',
                        }}
                    >
                        {zapierEmbedReady && zapierEmbedClientId && (
                            <zapier-workflow
                                sign-up-email={user?.emailAddress}
                                sign-up-first-name={user?.firstName}
                                sign-up-last-name={user?.lastName}
                                client-id={zapierEmbedClientId}
                                theme={themeName === 'light' ? 'light' : themeName === 'dark' ? 'dark' : 'auto'}
                                intro-copy-display='show'
                                manage-zaps-display='show'
                                guess-zap-display='show'
                                app-search-bar-display='show'
                                zap-create-from-scratch-display='show'
                            />
                        )}
                    </div>
                </div>
            </FlexContentRegion>
            <footer aria-label='Zapier workflows documentation' style={{ flex: 'none', paddingBottom: '1rem' }}>
                <Divider style={{ margin: '0 0 1rem' }} />
                <LearnMoreFooter topic='automating your workflows via Zapier' />
            </footer>
        </FullHeightFlexColumn>
    );
};

export default Workflows;
